// useTypewriter.ts
import { useEffect, useRef, useState } from "react";

/**
 * Smoothly reveals `source` one character at a time.
 * Uses refs for progress to avoid stale-closure issues in RAF.
 */
export function useTypewriter(source: string, cps = 60) {
  const [shown, setShown] = useState("");
  const targetRef = useRef(source);     // latest full target text
  const lenRef = useRef(0);             // how many chars are currently revealed
  const carryRef = useRef(0);           // fractional char accumulator for timing
  const rafRef = useRef<number | null>(null);

  // keep target current; clamp progress if source shrinks (rare)
  useEffect(() => {
    targetRef.current = source;
    if (lenRef.current > source.length) {
      lenRef.current = source.length;
      const slice = source.slice(0, lenRef.current);
      setShown(slice);
    }
  }, [source]);

  useEffect(() => {
    const step = (now: number) => {
      const target = targetRef.current;
      const curLen = lenRef.current;

      if (curLen < target.length) {
        // advance toward target length at cps
        // dt per frame: assume ~60fps (we don't need precise dt here)
        // Keep it robust by reading real dt between frames:
        // but to keep code minimal, we just add 1 frame's worth below.
      }

      // compute dt using performance.now across frames
      // store last timestamp in a ref to get smooth timing
    };

    // more robust: track dt accurately
    const lastRef = { current: performance.now() };

    const loop = (t: number) => {
      const dt = (t - lastRef.current) / 1000;
      lastRef.current = t;

      const target = targetRef.current;
      const curLen = lenRef.current;

      if (curLen < target.length) {
        carryRef.current += dt * cps;               // chars per second
        if (carryRef.current >= 1) {
          const n = Math.floor(carryRef.current);   // reveal up to n chars
          carryRef.current -= n;
          const nextLen = Math.min(target.length, curLen + n);
          lenRef.current = nextLen;
          setShown(target.slice(0, nextLen));
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [cps]);

  return shown;
}
